"use client";

import type { FC } from "react";
import type { ILink } from "@ui/components/core/Link";
import type { Media } from "@mw/shared/payload-types";
import type { DemoConfig } from "@/app/_blocks/BlockOmniboxSlides";

import { Link } from "@ui/components/core/Link";
import { AnimatePresence } from "motion/react";
import * as m from "motion/react-m";
import { ArrowUpRightIcon } from "lucide-react";
import clsx from "clsx";
import { useRef, useState } from "react";

import { DemoContent } from "@ui/components/demos/VoiceGeneratorDemos";
import { VideoPlayerContent } from "@ui/components/core/VideoPlayerContent";
import { HairlineCardBorder } from "@ui/components/core/HairlineCardBorder";
import { useInView } from "@ui/lib/useInView";
import { getImageProps } from "@utilities/payload";
import NextImage from "next/image";

// Discriminated union for content types
interface ComponentContent {
  type: "component";
  demo: DemoConfig;
}

interface VideoContent {
  type: "video";
  video?: Media;
  overlayTitle?: string;
  overlayDescription?: string;
}

interface ImageContent {
  type: "image";
  background?: Media;
  foreground?: Media;
  overlay?: Media;
  theme?: "dark" | "light" | "transparent";
}

export type OmniboxContent = ComponentContent | VideoContent | ImageContent;

interface OmniboxContentCardProps {
  animationKey?: number | string;
  className?: string;
  content: OmniboxContent;
  title?: string | null;
  description?: string | null;
  link?: ILink | null;
  textStyle?: "smallHeadings" | "largeHeadings";
}

export const OmniboxContentCard: FC<OmniboxContentCardProps> = ({
  animationKey,
  className,
  content,
  title,
  description,
  link,
  textStyle = "largeHeadings",
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Video state management
  const [isMuted, setIsMuted] = useState(true);
  const [showControls, setShowControls] = useState(true);

  // Check if component is in viewport for video autoplay
  const isInView = useInView(containerRef, { once: false, threshold: 0.1 });

  const toggleMute = () => setIsMuted(prev => !prev);
  const handleInteraction = () => setShowControls(true);

  // Video content renders differently - full bleed within the card
  if (content.type === "video") {
    if (!content.video) return null;

    return (
      <div
        ref={containerRef}
        className={clsx("tw-rounded-2xl tw-h-[34.375rem] tw-w-full tw-relative tw-overflow-hidden", className)}
      >
        {/* Background */}
        <div className='tw-absolute tw-inset-0 tw-z-0 tw-h-full tw-w-full'>
          <div className='tw-h-full tw-w-full tw-rounded-lg tw-bg-cream' />
        </div>

        {/* Full-bleed video container */}
        <div className='tw-absolute tw-inset-0 tw-z-10'>
          <VideoPlayerContent
            video={{
              title: content.overlayTitle ?? "",
              description: content.overlayDescription ?? "",
              media: content.video,
            }}
            videoRef={videoRef}
            isMuted={isMuted}
            showControls={showControls}
            inView={isInView}
            enableOverlay={Boolean(content.overlayTitle || content.overlayDescription)}
            objectFit='cover'
            onInteraction={handleInteraction}
            toggleMute={toggleMute}
          />
        </div>

        <HairlineCardBorder />
      </div>
    );
  }

  // Image content - full bleed within the card
  if (content.type === "image") {
    const foregroundProps = getImageProps(content.foreground, { fill: true, placeholder: true });
    if (!foregroundProps) return null;

    const backgroundProps = content.background ? getImageProps(content.background, { fill: true }) : null;
    const overlayProps = content.overlay ? getImageProps(content.overlay, { fill: true }) : null;
    const backgroundColor =
      content.theme === "dark" ? "#1E1916" : content.theme === "transparent" ? "transparent" : "#F7F4F2";

    // Overlay opacity varies based on whether there's a background image
    const overlayOpacity = content.background ? 0.28 : content.theme === "dark" ? 0.12 : 0.08;

    return (
      <div
        ref={containerRef}
        className={clsx("tw-rounded-2xl tw-h-[34.375rem] tw-w-full tw-relative tw-overflow-hidden", className)}
      >
        {/* Background with theme color and optional background image */}
        <AnimatePresence mode='popLayout'>
          <m.div
            key={`bg-${animationKey}`}
            className='tw-absolute tw-inset-0 tw-z-0 tw-h-full tw-w-full'
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1.05 }}
            exit={{ opacity: 0, scale: 1.05, transition: { duration: 0.3, delay: 0.3, ease: "easeInOut" } }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
          >
            {/* Theme color as base background */}
            <div className='tw-h-full tw-w-full' style={{ backgroundColor }} />
            {/* Background image overlays on theme color */}
            {backgroundProps && (
              <NextImage {...backgroundProps} className='tw-absolute tw-inset-0 tw-w-full tw-h-full tw-object-cover' />
            )}
          </m.div>
        </AnimatePresence>

        {/* Optional overlay layer - animates with background, uses mix-blend-mode */}
        {overlayProps && (
          <AnimatePresence mode='popLayout'>
            <m.div
              key={`overlay-${animationKey}`}
              className='tw-absolute tw-inset-0 tw-z-[5] tw-h-full tw-w-full'
              initial={{ opacity: 0, scale: 1.05 }}
              animate={{ opacity: overlayOpacity, scale: 1.05 }}
              exit={{ opacity: 0, scale: 1.05, transition: { duration: 0.3, ease: "easeInOut" } }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              style={{ mixBlendMode: content.background ? "overlay" : "normal" }}
            >
              <NextImage {...overlayProps} className='tw-absolute tw-inset-0 tw-w-full tw-h-full tw-object-contain' />
            </m.div>
          </AnimatePresence>
        )}

        {/* Foreground image with spring animation */}
        <AnimatePresence mode='wait'>
          <m.div
            key={`fg-${animationKey}`}
            className='tw-absolute tw-inset-0 tw-z-10'
            initial={{ opacity: 0, filter: "blur(5px)" }}
            animate={{ opacity: 1, filter: "blur(0px)" }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          >
            <NextImage {...foregroundProps} className='tw-absolute tw-inset-0 tw-w-full tw-h-full tw-object-cover' />
          </m.div>
        </AnimatePresence>

        {/* Gradient overlay for text readability - bottom position */}
        {(title || description) && (
          <div className='tw-absolute tw-inset-x-0 tw-bottom-0 tw-h-1/3 tw-pointer-events-none tw-z-20 tw-bg-gradient-to-t tw-from-black/60 tw-to-transparent' />
        )}

        {/* Title and description - bottom left, matching component footer position */}
        {(title || description) && (
          <div className='tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-z-30 tw-px-4 tw-pb-6 md:tw-px-6 md:tw-pb-8 lg:tw-px-7 lg:tw-pb-8 tw-flex tw-gap-12'>
            <div className='tw-flex tw-flex-1 tw-flex-col'>
              <div className='tw-mt-auto'>
                {title && (
                  <p
                    className={clsx(
                      "tw-mb-4",
                      textStyle === "smallHeadings"
                        ? "tw-type-sm tw-font-medium tw-text-white/60"
                        : "tw-type-xl tw-text-white",
                    )}
                  >
                    {title}
                  </p>
                )}
                {description && (
                  <p
                    className={clsx(
                      textStyle === "smallHeadings" ? "tw-type-base tw-text-white" : "tw-type-base tw-text-white/60",
                    )}
                  >
                    {description}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Link button - positioned on top of image */}
        {link && (
          <div className='tw-absolute tw-top-2 tw-right-2 md:tw-top-3 md:tw-right-3 lg:tw-top-2 lg:tw-right-2 tw-z-40'>
            <Link
              {...link}
              asButton
              buttonVariants={{ variant: "elevated", size: "pill-md" }}
              className='tw-effect-colors tw-group/link !tw-bg-white !tw-backdrop-blur-md tw-transition-opacity tw-duration-500 hover:!tw-bg-white'
            >
              <ArrowUpRightIcon className='tw-group-hover/link:tw-text-default tw-effect-colors' size={16} />
            </Link>
          </div>
        )}

        <HairlineCardBorder />
      </div>
    );
  }

  // At this point, content.type must be "component" (video and image have early returns)
  const componentContent = content as ComponentContent;

  return (
    <div
      ref={containerRef}
      className={clsx(
        "tw-rounded-2xl tw-h-[34.375rem] tw-w-full tw-flex tw-flex-col tw-group tw-relative tw-overflow-hidden",
        className,
      )}
    >
      <div className='tw-absolute tw-inset-0 tw-z-0 tw-h-full tw-w-full'>
        <div className='tw-h-full tw-w-full tw-rounded-lg tw-bg-cream' />
      </div>

      {/* Link button section */}
      {link && (
        <div className='tw-flex tw-justify-end tw-px-2 tw-pt-2 md:tw-px-3 md:tw-pt-3 lg:tw-px-2 lg:tw-pt-2 tw-relative tw-z-40'>
          <Link
            {...link}
            asButton
            buttonVariants={{ variant: "elevated", size: "pill-md" }}
            className='tw-effect-colors tw-group/link !tw-bg-white !tw-backdrop-blur-md tw-transition-opacity tw-duration-500 hover:!tw-bg-white'
          >
            <ArrowUpRightIcon className='tw-group-hover/link:tw-text-default tw-effect-colors' size={16} />
          </Link>
        </div>
      )}

      <div className='tw-w-full tw-px-4 md:tw-px-6 lg:tw-px-4 xl:tw-px-11 tw-flex-1 tw-min-h-0 tw-flex tw-items-center tw-justify-center tw-relative tw-z-10'>
        <AnimatePresence mode='wait'>
          <m.div
            key={animationKey}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, ease: "easeIn" }}
            className='tw-h-full tw-w-full tw-flex tw-items-center tw-justify-center'
          >
            <DemoContent demo={componentContent.demo} />
          </m.div>
        </AnimatePresence>
      </div>

      {/* Footer */}
      {(title || description) && (
        <div className='tw-px-4 tw-pb-6 md:tw-px-6 md:tw-pb-8 lg:tw-px-7 lg:tw-pb-8 tw-shrink-0 tw-relative tw-z-20 tw-flex tw-gap-12'>
          <div className='tw-flex tw-flex-1 tw-flex-col'>
            <div className='tw-mt-auto'>
              {title && (
                <p
                  className={clsx(
                    "tw-mb-4",
                    textStyle === "smallHeadings"
                      ? "tw-type-sm tw-font-medium tw-text-gray-500"
                      : "tw-type-xl tw-text-black",
                  )}
                >
                  {title}
                </p>
              )}
              {description && (
                <p
                  className={clsx(
                    textStyle === "smallHeadings" ? "tw-type-base tw-text-black" : "tw-type-base tw-text-gray-500",
                  )}
                >
                  {description}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      <HairlineCardBorder />
    </div>
  );
};

export default OmniboxContentCard;
